import { fetchPostTruelayerAuthorizationUrl } from '@iwoca/lapi-client/edge';
import { AlertBox, Button, CheckBox, Icon } from '@iwoca/orion';
import { useMutation } from '@tanstack/react-query';
import cn from 'classnames';
import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../../iwProvider/configureStore';
import {
    getQueryParameter,
    getWindowLocation,
    isLinkingOpenBankingOnly
} from '../../../../iwRouter/LocationHelper';
import {
    TRUELAYER_CANCEL,
    TRUELAYER_FAILURE,
    TRUELAYER_SUCCESS
} from './truelayer.constants';
import styles from './trueLayer.module.css';
import { TruelayerAlertBox } from './TruelayerAlertbox';

import { REVENUE_BASED_LOAN_PRODUCT_TYPE } from '../../../multiLoanDashboard/accountsSelector';
import { hasApplicationWithProductType } from '../../../../api/lendingAPI/ApplicationSelector';
import messages from './TrueLayer.messages';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl
} from 'react-intl';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import TrueLayerLinkedAccounts from './TrueLayerLinkedAccounts';
import { useSplitTests } from '@api/lendingAPI/lapiHooks';
import ManualUpload from '../../../applyFlexiLoanView/applyFlexiLoanFormView/NewDocUploads/RequirementTypes/BankStatements/ManualUpload';
import BankStatementsMessages from '../../../applyFlexiLoanView/applyFlexiLoanFormView/NewDocUploads/RequirementTypes/BankStatements/BankStatements.messages';

const mapStateToProps = (state: RootState) => {
    return {
        isRBLApplication: hasApplicationWithProductType(
            state,
            REVENUE_BASED_LOAN_PRODUCT_TYPE
        ),
        stateKey:
            state.siteContext.impersonatingStateKey ||
            state.siteContext.state_key
    };
};

interface Props {
    handleClose?: () => void;
    handleUploadBankStatements: () => void;
}

type PropsFromRedux = ReturnType<typeof mapStateToProps> & Props;

export type AlertStatusTypes =
    | typeof TRUELAYER_SUCCESS
    | typeof TRUELAYER_FAILURE
    | typeof TRUELAYER_CANCEL;

// TODO: write a test for trading data to open modal on truelayer url
// TODO: storybook for different states
const TrueLayer = ({
    handleClose,
    handleUploadBankStatements,
    isRBLApplication,
    stateKey,
    intl
}: PropsFromRedux & WrappedComponentProps) => {
    const { isLoading, mutate: fetchTruelayerUrl } = useMutation({
        mutationFn: () =>
            fetchPostTruelayerAuthorizationUrl({ stateKey: stateKey! }),
        onSuccess: (data) => {
            if (data?.data?.authorization_url) {
                window.location.href = data.data.authorization_url;
            }
        },
        onError: () => {
            setAlertStatus(TRUELAYER_FAILURE);
        }
    });

    const [alertStatus, setAlertStatus] = useState<AlertStatusTypes>();

    const [showManualPDF, setShowManualPDF] = useState(false);

    const DEAutoBSSplitTest = useSplitTests('de_auto_bs_required_test_2024');

    const isInLinkOBBranchDE =
        (DEAutoBSSplitTest as unknown as string) === 'OB_REQUIRED';

    useEffect(() => {
        const location = getWindowLocation();
        const statusCode = getQueryParameter(location, 'status_code');
        const errorDetail = getQueryParameter(location, 'error_detail');
        if (statusCode) {
            if (statusCode === '200') {
                return setAlertStatus(TRUELAYER_SUCCESS);
            }
            if (
                statusCode === '500' &&
                errorDetail &&
                errorDetail === 'access_denied'
            ) {
                return setAlertStatus(TRUELAYER_CANCEL);
            }
            if (statusCode === '500' && errorDetail !== 'access_denied') {
                return setAlertStatus(TRUELAYER_FAILURE);
            }
            return setAlertStatus(TRUELAYER_FAILURE);
        }
    }, []);

    return (
        <div data-testid="truelayer" className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.heading}>
                    <FormattedMessage {...messages.chooseBankAccount} />
                </h2>
                {handleClose && (
                    <button
                        className={styles.closeModalButton}
                        onClick={() => handleClose()}
                        aria-label="close-button"
                        title="close modal button"
                    >
                        <Icon icon="timesDefault" width={34} height={34} />
                    </button>
                )}
            </div>

            <TrueLayerLinkedAccounts />

            {alertStatus && (
                <TruelayerAlertBox
                    handleFailureButtonClick={handleUploadBankStatements}
                    handleCancelButtonClick={handleUploadBankStatements}
                    status={alertStatus}
                />
            )}

            <p className={styles.body}>
                <FormattedMessage
                    {...messages.info}
                    values={{
                        link: (
                            <a
                                className={styles.link}
                                href={intl.formatMessage(messages.infoLink)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FormattedMessage {...messages.infoLinkText} />
                            </a>
                        )
                    }}
                />
            </p>

            <Button
                variant="primary"
                className={styles.ctaButton}
                role="button"
                onClick={() => fetchTruelayerUrl()}
                loading={isLoading}
                disabled={showManualPDF}
            >
                <FormattedMessage {...messages.connectButton} />
            </Button>

            {!showManualPDF && (
                <AlertBox variant="connect" className={styles.alertBox}>
                    <Icon
                        icon={getCountry() === 'DE' ? 'euro' : 'pound'}
                        width={20}
                        height={20}
                        className={styles.connectAlertBoxIcon}
                    />
                    <span
                        className={cn(
                            styles.connectAlertBoxTextContent,
                            styles.alertBoxContent
                        )}
                    >
                        <FormattedMessage {...messages.redirect} />
                    </span>
                </AlertBox>
            )}

            <div className={styles.footer}>
                {!isLinkingOpenBankingOnly() &&
                    !isRBLApplication &&
                    !isInLinkOBBranchDE && (
                        <FormattedMessage
                            {...messages.pdfOption}
                            values={{
                                openModal: (
                                    <span
                                        data-testid="upload-bank-statements"
                                        className={styles.link}
                                        onClick={handleUploadBankStatements}
                                    >
                                        <FormattedMessage
                                            {...messages.uploadStatements}
                                        />
                                    </span>
                                )
                            }}
                        />
                    )}

                {showManualPDF && (
                    <div className={styles.manualUploadContainer}>
                        <h2 className={styles.header}>
                            <FormattedMessage
                                {...BankStatementsMessages.manualUploadTitle}
                            />
                        </h2>
                        <ManualUpload />
                    </div>
                )}

                {isInLinkOBBranchDE && (
                    <CheckBox
                        label={intl.formatMessage(messages.cannotLinkAccount)}
                        wrapperClassName="mx-auto my-auto"
                        onClick={() => {
                            setShowManualPDF((prevState) => !prevState);
                        }}
                    />
                )}

                {getCountry() === 'UK' && (
                    <p>
                        <FormattedMessage
                            {...messages.fcaLinkText}
                            values={{
                                link: (
                                    <a
                                        className={styles.link}
                                        href="https://register.fca.org.uk/s/firm?id=001b00000413NAxAAM"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FormattedMessage
                                            {...messages.fcaLink}
                                        />
                                    </a>
                                )
                            }}
                        />
                    </p>
                )}
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(injectIntl(TrueLayer));
